import { ComponentType, Fragment, useMemo } from "react";
import parse, { DOMNode, Element, Text } from "html-react-parser";
import DOMPurify from "dompurify";
import { splitArticleCards } from "../../../../Helpers/Read/ArticleCards";
import ArticleVideoCard from "./ArticleVideoCard";

// Register future card types/versions here; stored content remains portable HTML.
const cardRenderers: Record<string, ComponentType<{ id: number }>> = {
    "video:1": ArticleVideoCard,
};
const literalContainers = new Set(["code", "pre", "a", "button", "textarea", "script", "style"]);

function isLiteralContent(node: DOMNode): boolean {
    let parent = node.parent;
    while (parent) {
        if (parent instanceof Element && literalContainers.has(parent.name)) return true;
        parent = parent.parent;
    }
    return false;
}

function normalizeLegacyHTML(content: string): string {
    const unquoted = content.startsWith('"') && content.endsWith('"') ? content.slice(1, -1) : content;
    // Some old articles stored the entire HTML escaped. Decode those once while
    // preserving literal entities inside regular HTML (for example code samples).
    if (!/<[a-z][\s\S]*>/i.test(unquoted) && /&lt;\/?[a-z][a-z0-9]*\b/i.test(unquoted)) {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = unquoted;
        return textarea.value;
    }
    return unquoted;
}

export default function ArticleContent({ html }: { html: string }) {
    const content = useMemo(() => {
        const clean = DOMPurify.sanitize(normalizeLegacyHTML(html));
        return parse(clean, {
            replace(node) {
                if (!(node instanceof Text) || isLiteralContent(node)) return;
                const parts = splitArticleCards(node.data);
                if (!parts.some((part) => typeof part !== "string")) return;
                return <>{parts.map((part, index) => {
                    if (typeof part === "string") return <Fragment key={index}>{part}</Fragment>;
                    const Card = cardRenderers[`${part.type}:${part.version}`];
                    return Card
                        ? <Card key={index} id={part.id} />
                        : <span key={index} className="article-card article-card--placeholder">This embedded content is unavailable.</span>;
                })}</>;
            },
        });
    }, [html]);

    return <>{content}</>;
}
